import React from 'react';
import { Audio, Sequence, useCurrentFrame, useVideoConfig } from 'remotion';
import type { AudioItem, ProjectModel } from '@video-studio/shared';
import { berekenVolume } from './VideoLaag.js';

/**
 * Audiolaag.
 *
 * `<Audio>` van remotion werkt zowel in de browser (preview) als tijdens het
 * renderen. Ontbreekt de media, dan renderen we niets: een ontbrekend
 * audiobestand mag nooit een export laten mislukken, maar moet wel zichtbaar
 * zijn in de UI (de API geeft per asset een `beschikbaar`-vlag terug).
 */
export interface AudioLaagProps {
  item: AudioItem;
  project: ProjectModel;
  assetUrls: Record<string, string>;
}

export const AudioLaag: React.FC<AudioLaagProps> = ({ item, assetUrls }) => {
  const url = assetUrls[item.assetId];
  const trimBefore = Math.max(0, Math.round(item.bronStartFrame));
  if (!url) return null;

  return (
    <Sequence
      from={Math.max(0, Math.round(item.startFrame))}
      durationInFrames={Math.max(1, Math.round(item.duurInFrames))}
      name={item.naam || 'Audio'}
    >
      <AudioInSequence item={item} url={url} trimBefore={trimBefore} />
    </Sequence>
  );
};

const AudioInSequence: React.FC<{ item: AudioItem; url: string; trimBefore: number }> = ({
  item,
  url,
  trimBefore,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const volume = berekenVolume(item, frame, fps);
  return <Audio src={url} trimBefore={trimBefore} volume={item.gedempt ? 0 : volume} muted={item.gedempt} />;
};
